import {Eye} from "lucide-react";
import {useMemo, useState} from "react";

import {Alert, AlertDescription, AlertTitle} from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {Badge} from "@/components/ui/badge";
import {Button} from "@/components/ui/button";
import {formatDate, formatTime} from "@/src/modules/core/lib/formatters";
import {useStoredMe} from "@/src/modules/core/store/me";

import useEndImpersonation from "../hooks/use-end-impersonation";

export default function ImpersonationBanner() {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const me = useStoredMe();
  const {mutateAsync: endImpersonation, isPending} = useEndImpersonation();
  const impersonation = me?.impersonation;

  const targetLabel = useMemo(() => {
    if (!impersonation?.target) return "";
    const {display_name, user_name} = impersonation.target;
    return display_name ? `${display_name} (${user_name})` : user_name;
  }, [impersonation?.target]);

  if (!impersonation?.active) return null;

  const handleConfirmEnd = async () => {
    try {
      await endImpersonation();
    } catch {
      // errorHandler on mutation
    }
  };

  return (
    <>
      <Alert
        variant="warning"
        role="status"
        aria-live="polite"
        className="rounded-none border-x-0 border-t-0 border-b-amber-400/80 bg-amber-50 px-4 py-2.5 dark:bg-amber-950/35 md:px-6"
      >
        <Eye className="size-4 shrink-0 text-amber-700 dark:text-amber-400" />
        <div className="col-start-2 flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
          <div className="flex min-w-0 flex-col gap-0.5">
            <AlertTitle className="line-clamp-none flex min-h-0 flex-wrap items-center gap-2 whitespace-normal leading-snug text-amber-950 dark:text-amber-100">
              Viewing as <span className="text-primary font-semibold">{targetLabel}</span>
              {impersonation.read_only ? (
                <Badge variant="warning" className="text-xs">
                  Read-only
                </Badge>
              ) : null}
            </AlertTitle>
            <AlertDescription className="text-xs tabular-nums text-amber-800/90 dark:text-amber-200/80">
              Expires {formatDate(impersonation.expires_at)} {formatTime(impersonation.expires_at)}
            </AlertDescription>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0 border-amber-300/80 bg-background/90 hover:bg-background dark:border-amber-700/60"
            disabled={isPending}
            onClick={() => setConfirmOpen(true)}
          >
            {isPending ? "Ending…" : "End impersonation"}
          </Button>
        </div>
      </Alert>

      <AlertDialog
        open={confirmOpen}
        onOpenChange={(next) => {
          if (!isPending) setConfirmOpen(next);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>End impersonation?</AlertDialogTitle>
            <AlertDialogDescription>
              You will return to your admin account
              {targetLabel ? ` and stop viewing as ${targetLabel}` : ""}. The app
              will reload to restore your permissions and data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
            <Button
              type="button"
              disabled={isPending}
              onClick={() => void handleConfirmEnd()}
            >
              {isPending ? "Ending…" : "End impersonation"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
